import { useState, useEffect, useCallback } from "react";
import { t, font, Button, Input, Badge } from "@realtyflows/ui";
import {
  getFolderCompliance,
  updateComplianceItem,
  addComplianceRequirement,
  aiSuggestMissingDocs,
} from "../api";

/**
 * FolderCompliance — V2 Feature 2.2
 *
 * Required-doc checklist per folder. Each item is one of:
 *   missing  → not present yet (red)
 *   uploaded → satisfied by a form/upload (sky)
 *   approved → broker-verified (emerald)
 *   waived   → explicitly skipped with reason (muted)
 *
 * Items auto-sync to "uploaded" when a matching form/upload exists.
 * Status changes require editor access.
 */

const STATUS_META = {
  missing:  { label: "Missing",  color: "coral" },
  uploaded: { label: "Uploaded", color: "sky" },
  approved: { label: "Approved", color: "emerald" },
  waived:   { label: "Waived",   color: "muted" },
};
const colorOf = (n) => ({ coral: t.coral, sky: t.sky, emerald: t.emerald, amber: t.amber, muted: t.textMuted })[n] || t.textMuted;

export default function FolderCompliance({ folderId }) {
  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newReqName, setNewReqName] = useState("");
  const [newReqAliases, setNewReqAliases] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // V2 Feature 2.1: AI-suggested missing docs (with "why this matters" explanations)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiItems, setAiItems] = useState(null);   // null = not asked yet, [] = no suggestions

  const handleAiSuggest = async () => {
    setAiLoading(true);
    try {
      const result = await aiSuggestMissingDocs(folderId);
      setAiItems(result?.items || []);
    } catch (err) {
      window.alert(err.message || "AI suggestions unavailable");
      setAiItems([]);
    } finally {
      setAiLoading(false);
    }
  };

  const refresh = useCallback(() => {
    if (!folderId) return;
    setLoading(true);
    getFolderCompliance(folderId)
      .then(setData)
      .catch(() => setData({ items: [], summary: null }))
      .finally(() => setLoading(false));
  }, [folderId]);
  useEffect(refresh, [refresh]);

  const handleStatusChange = async (item, status) => {
    setBusyId(item.id);
    let waived_reason = null;
    if (status === "waived") {
      waived_reason = window.prompt("Why are you waiving this requirement?", item.waived_reason || "");
      if (waived_reason === null) { setBusyId(null); return; } // cancelled
    }
    try {
      await updateComplianceItem(folderId, item.id, { status, waived_reason });
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not update");
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async () => {
    if (!newReqName.trim()) return;
    setAdding(true);
    try {
      const aliases = newReqAliases.split(",").map((s) => s.trim()).filter(Boolean);
      await addComplianceRequirement(folderId, { name: newReqName.trim(), doc_aliases: aliases, required: true });
      setNewReqName("");
      setNewReqAliases("");
      setShowAdd(false);
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not add");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return <div style={{ fontSize: 12, color: t.textMuted, padding: 10, fontFamily: font.body }}>Loading compliance...</div>;
  }

  const s = data.summary || {};
  const headerColor = s.percentComplete === 100 ? t.emerald : s.percentComplete >= 60 ? t.amber : t.coral;

  return (
    <div>
      {/* Summary header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: headerColor, fontFamily: font.mono, lineHeight: 1 }}>
              {s.requiredSatisfied || 0}
            </span>
            <span style={{ fontSize: 12, color: t.textMuted, fontFamily: font.mono }}>
              / {s.requiredTotal || 0} required complete
            </span>
          </div>
          <div style={{ height: 4, background: t.border, borderRadius: 99, marginTop: 6, overflow: "hidden" }}>
            <div style={{
              width: `${s.percentComplete || 0}%`, height: "100%",
              background: headerColor, transition: "width 0.3s",
            }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="outline" size="sm" onClick={handleAiSuggest} disabled={aiLoading}>
            {aiLoading ? "Thinking..." : "✨ AI suggest"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Cancel" : "+ Add"}
          </Button>
        </div>
      </div>

      {/* V2 Feature 2.1: AI suggestions panel */}
      {aiItems && (
        <div style={{
          marginBottom: 14, padding: 14, borderRadius: 10,
          background: `linear-gradient(135deg, ${t.purple}08, ${t.emerald}08)`,
          border: `1px solid ${t.purple}30`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.purple || t.textPrimary, fontFamily: font.display }}>
              ✨ AI Compliance Review
            </span>
            <button
              onClick={() => setAiItems(null)}
              style={{ background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 16, lineHeight: 1 }}
            >×</button>
          </div>
          {aiItems.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
              Everything looks good — no additional missing documents found.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {aiItems.map((it, idx) => {
                const urgencyColor = it.urgency === "high" ? t.coral : it.urgency === "medium" ? t.amber : t.textMuted;
                return (
                  <div key={idx} style={{
                    padding: "8px 10px", background: t.surface,
                    border: `1px solid ${t.border}`, borderRadius: 6,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                        {it.name}
                      </span>
                      <Badge color={urgencyColor}>{it.urgency || "medium"}</Badge>
                      {it.source === "ai" && <Badge color={t.purple || t.sky}>AI suggested</Badge>}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, lineHeight: 1.4 }}>
                      {it.why}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Add-requirement form */}
      {showAdd && (
        <div style={{ marginBottom: 14, padding: 12, background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Input placeholder="Requirement name (e.g. HOA Disclosure)" value={newReqName} onChange={(e) => setNewReqName(e.target.value)} />
            <Input
              placeholder="Match keywords (comma-separated, e.g. hoa, association)"
              value={newReqAliases}
              onChange={(e) => setNewReqAliases(e.target.value)}
            />
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={adding || !newReqName.trim()}>
              {adding ? "Adding..." : "Add requirement"}
            </Button>
          </div>
        </div>
      )}

      {/* Items list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.items.map((item) => {
          const meta = STATUS_META[item.status] || STATUS_META.missing;
          const color = colorOf(meta.color);
          return (
            <div key={item.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              background: item.status === "approved" ? t.emerald + "08" : t.surface,
              border: `1px solid ${item.status === "missing" ? t.coral + "40" : t.border}`,
              borderRadius: 8,
            }}>
              {/* Status pill (clickable to advance through workflow) */}
              <div style={{
                width: 18, height: 18, borderRadius: 99,
                background: item.status === "missing" ? "transparent" : color,
                border: `2px solid ${color}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>
                {item.status === "approved" && "✓"}
                {item.status === "waived" && "−"}
                {item.status === "uploaded" && "•"}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body,
                  textDecoration: item.status === "approved" || item.status === "waived" ? "none" : "none",
                }}>
                  {item.name}
                  {!item.required && (
                    <span style={{ fontSize: 10, color: t.textMuted, marginLeft: 8, fontFamily: font.body }}>(optional)</span>
                  )}
                </div>
                {item.status === "waived" && item.waived_reason && (
                  <div style={{ fontSize: 10, color: t.textMuted, fontStyle: "italic", fontFamily: font.body }}>
                    Waived: {item.waived_reason}
                  </div>
                )}
                {item.status === "approved" && item.approved_by_name && (
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                    Approved by {item.approved_by_name}
                  </div>
                )}
              </div>

              <Badge color={color}>{meta.label}</Badge>

              <select
                value={item.status}
                onChange={(e) => handleStatusChange(item, e.target.value)}
                disabled={busyId === item.id}
                style={{
                  fontSize: 11, padding: "4px 6px", borderRadius: 6,
                  border: `1px solid ${t.border}`, background: t.surfaceWarm,
                  color: t.textPrimary, fontFamily: font.body, cursor: "pointer",
                }}
              >
                {Object.entries(STATUS_META).map(([key, m]) => (
                  <option key={key} value={key}>{m.label}</option>
                ))}
              </select>
            </div>
          );
        })}
        {data.items.length === 0 && (
          <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 10, fontFamily: font.body }}>
            No requirements configured for this folder type yet.
          </div>
        )}
      </div>
    </div>
  );
}
